
























import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { TalentAgentSearchResult } from "@scrile/api-provider/dist/api/TalentAgentsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePromisesStack from "@/hooks/usePromisesStack";
import providers, { getManagerProviders } from "@/lib/api";
import { setLoginData } from "@/lib/authHelper";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseSpinner from "@/components/BaseSpinner";
import ModalSwitchAccount from "../ModalSwitchAccount";

export default defineComponent({
  name: "MoreProfileHeader",
  components: { ModalSwitchAccount, BaseAvatar, BaseSpinner, BaseIcon },
  setup() {
    const { authUserStrict, authUser, isTalentAgent } = useAuthUser();
    const { loading, executeAfterAll, executeAsyncAfterAll } = usePromisesStack();
    let { providers: managerProviders, apiTools: managerApiTools } = getManagerProviders();

    const showModalSwitchAccount = ref(false);
    const loadingUserId = ref("");
    const creatorsMessages = ref<Record<string, number>>({});
    const searchManagedCreators = ref<TalentAgentSearchResult | null>(null);
    const managerLongLiveToken = ref("");
    const managerId = ref(authUserStrict.value.id);
    const isManagerProviders = ref(false);

    const canManageUsers = computed(() => isTalentAgent.value || isManagerProviders.value);
    const managedCreators = computed(() => {
      const result: any[] = [];
      if (searchManagedCreators.value?.result.length) {
        result.push(searchManagedCreators.value.result[0].agent);
        searchManagedCreators.value.result.forEach(item => result.push(item.user));
      }
      return result;
    });

    const checkManagerProviders = async () => {
      managerLongLiveToken.value = await managerProviders.LoginProvider.getLongLiveToken();
      const longLiveToken = await providers.LoginProvider.getLongLiveToken();

      if (isTalentAgent.value) {
        await managerProviders.LoginProvider.setLongLiveToken(longLiveToken);
        managerApiTools.close();
        managerProviders = providers;
        isManagerProviders.value = false;
        return;
      }

      if (managerLongLiveToken.value) {
        isManagerProviders.value = true;
        const response = await managerProviders.LoginProvider.refreshTokenV2({ token: managerLongLiveToken.value });
        managerId.value = response.user.id;
      } else {
        managerApiTools.close();
      }
    };

    const getUnreadMessages = async () => {
      if (!managedCreators.value.length) return;
      const response = await managerProviders.MessageThreadsProvider.getUnreadMessageStatistic(
        managedCreators.value.map(i => i.id)
      );
      const newMessages: Record<string, number> = {};
      response.forEach(i => (newMessages[i.userId] = i.unreadMessageCount));
      creatorsMessages.value = newMessages;
    };

    const handleSwapButtonClick = async () => {
      showModalSwitchAccount.value = !showModalSwitchAccount.value;
      await executeAsyncAfterAll(getUnreadMessages);
    };

    const handleAccountClick = async (user: UserPublicData) => {
      if (loadingUserId.value === "") {
        try {
          loadingUserId.value = user.id;
          let token: string;
          if (user.id === searchManagedCreators.value?.result[0].agent?.id) {
            token = await managerProviders.LoginProvider.getLongLiveToken();
          } else {
            token = await managerProviders.LoginProvider.longLiveTokenByUser(user.id);
          }
          await providers.LoginProvider.setLongLiveToken(token);
          const loginResponse = await providers.LoginProvider.refreshTokenV2({ token });
          setLoginData({ user: loginResponse.user, authorities: loginResponse.authorities });
          location.reload();
        } finally {
          loadingUserId.value = "";
        }
      }
    };

    const getManagedCreators = async () => {
      await checkManagerProviders();
      if (canManageUsers.value) {
        searchManagedCreators.value = await managerProviders.TalentAgentsProvider.search({
          data: {
            agentId: managerId.value,
            size: 1000,
          },
        });
      }

      if (managerProviders !== providers && searchManagedCreators.value?.result.length === 0) {
        await managerProviders.LoginProvider.logout({ revokeToken: false });
      }
    };

    const getData = async () => {
      await executeAsyncAfterAll(getManagedCreators);
    };

    getData();

    onBeforeUnmount(async () => {
      if (managerProviders !== providers) {
        await executeAfterAll(() => managerApiTools.close());
      }
    });

    return {
      loading,
      loadingUserId,
      canManageUsers,
      authUser,
      managedCreators,
      creatorsMessages,
      isManagerProviders,
      showModalSwitchAccount,
      handleAccountClick,
      handleSwapButtonClick,
    };
  },
});
